<script setup lang="ts">
import { mergeDefaults as __MACROS_mergeDefaults } from "vue";
import { useMemoize } from "@vueuse/core"
import { getSvgPath } from "figma-squircle"

/**
 * This renders a smoother and more satisfying rounded rectangle
 * that can be used anywhere in the application. We render the
 * squircle as a clip path, ensuring to memoize it to make future
 * invocations or re-renders faster. Several hacks are required to
 * make this work in practice:
 *
 * - We wrap the element in a div with a border radius so that css
 *   will work on the outer element, such as shadows
 * - To render a border, we have to render an svg with the same path.
 *   The svg must have a stroke width that is the user-desired one times
 *   two, since strokes are drawn in the center of the path, and half of
 *   the stroke will be cut off by the clip path.
 * - The clip path must be generated based on the width and height of the
 *   container, so we use `useElementSize` to get the dimensions and
 *   pass them to the `getSvgPath` function reactively
 */

defineProps(__MACROS_mergeDefaults({
  cornerRadius: null,
  topLeftCornerRadius: null,
  topRightCornerRadius: null,
  bottomLeftCornerRadius: null,
  bottomRightCornerRadius: null,
  cornerSmoothing: null,
  stroke: null,
  strokeWidth: null
}, { cornerSmoothing: 0.6, }))
const container = useTemplateRef("container")
const { width, height } = useElementSize(container)

const svgPath = useMemoize(getSvgPath)

const clipPath = computed(() => svgPath({
  width: width.value,
  height: height.value,
  topLeftCornerRadius: __props.topLeftCornerRadius,
  topRightCornerRadius: __props.topRightCornerRadius,
  bottomLeftCornerRadius: __props.bottomLeftCornerRadius,
  bottomRightCornerRadius: __props.bottomRightCornerRadius,
  cornerRadius: __props.cornerRadius,
  cornerSmoothing: __props.cornerSmoothing,
}))

const outerStyle = computed(() => ({
  borderRadius: `${px(__props.topLeftCornerRadius ?? __props.cornerRadius ?? 0)} ${px(__props.topRightCornerRadius ?? __props.cornerRadius ?? 0)} ${px(__props.bottomRightCornerRadius ?? __props.cornerRadius ?? 0)} ${px(__props.bottomLeftCornerRadius ?? __props.cornerRadius ?? 0)}`,
}))
</script>

<template>
  <div
    class="outer"
    :style="outerStyle"
  >
    <div
      ref="container"
      :style="{ clipPath: `path('${clipPath}')` }"
      class="interface-squircle"
    >
      <svg
        v-if="strokeWidth && stroke"
        :width="width"
        :height="height"
        class="interface-squircle-shape"
      >
        <path
          :d="clipPath"
          :stroke
          :stroke-width="strokeWidth * 2"
          fill="transparent"
        />
      </svg>
      <slot />
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.interface-squircle
  @apply

.interface-squircle-shape
  @apply absolute pointer-events-none
</style>
